<template>
  <Toast />
  <ConfirmDialog />

  <div class="cocina-container">
    <div class="cocina-header">
      <div class="flex justify-content-between align-items-center">
        <div class="flex align-items-center gap-3">
          <i class="pi pi-book text-white" style="font-size: 2rem"></i>
          <div>
            <h1 class="m-0 text-white" style="font-size: 1.5rem">
              🍳 PEDIDOS DE COCINA
            </h1>
            <p class="m-0 text-white-alpha-80" style="font-size: 0.85rem">
              Sistema de visualización de pedidos
            </p>
          </div>
        </div>
        <div class="flex align-items-center gap-2">
          <Button
            icon="pi pi-refresh"
            label="Actualizar"
            class="p-button-sm p-button-warning"
            @click="cargarPedidos(false)"
            :loading="cargando"
          />
          <span class="text-white-alpha-80 font-bold" style="font-size: 1rem">
            {{ horaActual }}
          </span>
          <Button
            icon="pi pi-sign-out"
            class="p-button-sm p-button-danger"
            @click="salirDialog = true"
            v-tooltip.bottom="'Cerrar Sesión'"
          />
        </div>
      </div>
    </div>

    <div class="cocina-filtros">
      <div class="flex gap-2 flex-wrap">
        <Button
          :label="`Todos (${pedidosTodos.length})`"
          :class="filtro === 'todos' ? 'btn-filtro-activo' : 'btn-filtro'"
          class="p-button-sm"
          @click="filtro = 'todos'"
        />
        <Button
          :label="`🟠 En Cocina (${pedidosEnCocina.length})`"
          :class="
            filtro === 'cocina' ? 'btn-filtro-pendiente-activo' : 'btn-filtro'
          "
          class="p-button-sm"
          @click="filtro = 'cocina'"
        />
        <Button
          :label="`🟢 Listos (${pedidosListos.length})`"
          :class="
            filtro === 'listos' ? 'btn-filtro-listo-activo' : 'btn-filtro'
          "
          class="p-button-sm"
          @click="filtro = 'listos'"
        />
      </div>
    </div>

    <div
      v-if="cargando && pedidos.length === 0"
      class="flex justify-content-center align-items-center"
      style="min-height: 400px"
    >
      <ProgressSpinner />
    </div>

    <div v-else class="grid pedidos-grid">
      <div v-if="pedidosFiltrados.length === 0" class="col-12">
        <div class="text-center p-5">
          <i class="pi pi-check-circle text-6xl text-green-500 mb-3"></i>
          <h3 class="text-700">
            No hay pedidos {{ filtro === "cocina" ? "en cocina" : filtro }}
          </h3>
        </div>
      </div>

      <TransitionGroup name="list" tag="div" class="contents">
        <div
          v-for="pedido in pedidosFiltrados"
          :key="pedido.id"
          class="col-12 md:col-6 lg:col-4 mb-3"
        >
          <div :class="['pedido-card', getEstadoClass(pedido)]">
            <div class="pedido-card-header">
              <div class="flex justify-content-between align-items-start mb-2">
                <div class="flex-1">
                  <div class="pedido-mesa-numero-linea">
                    <span class="mesa-texto">
                      {{
                        pedido.mesa
                          ? `Mesa ${pedido.mesa.numero}`
                          : "Para Llevar"
                      }}
                    </span>
                    <span class="separador">-</span>
                    <span class="numero-pedido"
                      >#{{ pedido.nro_pedido || pedido.id }}</span
                    >
                  </div>
                  <div class="pedido-tiempo">
                    <i class="pi pi-user text-xs mr-1"></i>
                    <span class="font-semibold mr-2">{{
                      pedido.cliente?.nombre || "S/N"
                    }}</span>
                    <i class="pi pi-clock mr-1"></i>
                    {{ getTiempoTranscurrido(pedido.created_at) }}
                  </div>

                  <div class="flex gap-1 mt-1">
                    <div v-if="pedido.venta_id" class="pedido-cobrado-badge">
                      <i class="pi pi-check-circle mr-1"></i>
                      COBRADO
                    </div>
                    <div v-if="pedido.estado === 3" class="pedido-listo-badge">
                      <i class="pi pi-check mr-1"></i>
                      LISTO
                    </div>
                  </div>
                </div>

                <div :class="['pedido-badge', getEstadoBadgeClass(pedido)]">
                  {{ getEstadoTexto(pedido) }}
                </div>
              </div>
            </div>

            <div class="pedido-items">
              <div
                v-for="(item, index) in pedido.items"
                :key="index"
                class="pedido-item"
              >
                <div class="flex align-items-center">
                  <div class="flex-1">
                    <div class="flex align-items-center flex-wrap">
                      <span class="item-cantidad">{{ item.cantidad }}x</span>
                      <span class="item-nombre">{{
                        item.producto?.descripcion
                      }}</span>
                    </div>
                    <div v-if="item.nota_cocina" class="item-nota">
                      <i class="pi pi-comment"></i> {{ item.nota_cocina }}
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="pedido.nota_cliente" class="pedido-nota-general">
                <i class="pi pi-info-circle"></i>
                <strong>NOTA:</strong> {{ pedido.nota_cliente }}
              </div>
            </div>
            <div
              v-if="pedido.venta_id && pedido.estado !== 3 && !omitirCocina"
              class="pedido-footer p-2 border-top-1 border-gray-200"
            >
              <Button
                label="MARCAR LISTO"
                icon="pi pi-check"
                class="p-button-success w-full font-bold"
                :loading="marcandoListo[pedido.id]"
                :disabled="marcandoListo[pedido.id]"
                @click="marcarListo(pedido)"
              />
            </div>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </div>

  <Dialog
    v-model:visible="salirDialog"
    :style="{ width: '420px' }"
    header="Cerrar Sesión"
    :modal="true"
    class="p-fluid logout-dialog"
    :showHeader="true"
    :closable="true"
  >
    <div class="dialog-content">
      <div class="dialog-icon">
        <i class="pi pi-exclamation-circle" style="font-size: 5rem"></i>
      </div>
      <p class="dialog-message">
        ¿Estás seguro de que deseas cerrar tu sesión?
      </p>
    </div>

    <template #footer>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        class="p-button-outlined p-button-secondary"
        @click="salirDialog = false"
      />
      <Button
        label="Cerrar Sesión"
        icon="pi pi-sign-out"
        class="p-button-danger"
        @click="cerrarSession()"
      />
    </template>
  </Dialog>
</template>

<script>
import RestauranteService from "@/service/RestauranteService";
import { useAuth } from "@/stores";

const audioNotificacion = new Audio(
  "https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3",
);

export default {
  name: "VistaCocina",
  data() {
    return {
      pedidos: [],
      cargando: false,
      filtro: "todos",
      marcandoListo: {},
      horaActual: "",
      intervaloActualizar: null,
      intervaloHora: null,
      primeraCarga: true,
      salirDialog: false,
      documentVisible: true,
      omitirCocina: false,
    };
  },
  created() {
    this.restauranteService = new RestauranteService();
    this.auth = useAuth();
  },
  async mounted() {
    await this.cargarPedidos(false);
    this.actualizarHora();
    this.primeraCarga = false;

    this.intervaloActualizar = setInterval(() => {
      this.cargarPedidos(true);
    }, 5000);

    this.intervaloHora = setInterval(() => {
      this.actualizarHora();
      this.$forceUpdate();
    }, 60000);

    // NUEVO: Detectar cuando el tab/ventana está visible
    this.handleVisibilityChange = () => {
      this.documentVisible = !document.hidden;
    };
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  },
  beforeUnmount() {
    if (this.intervaloActualizar) clearInterval(this.intervaloActualizar);
    if (this.intervaloHora) clearInterval(this.intervaloHora);

    // NUEVO: Limpiar listener
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
  },
  computed: {
    pedidosTodos() {
      return [...this.pedidos]
        .filter((p) => p.items && p.items.length > 0)
        .sort((a, b) => b.id - a.id);
    },
    pedidosEnCocina() {
      return this.pedidosTodos.filter((p) => p.estado == 1);
    },
    pedidosFiltrados() {
      switch (this.filtro) {
        case "cocina":
          return this.pedidosEnCocina;
        case "listos":
          return this.pedidosListos;
        default:
          return this.pedidosTodos;
      }
    },
    pedidosListos() {
      return this.pedidosTodos.filter((p) => p.estado == 3);
    },
  },
  methods: {
    async marcarListo(pedido) {
      this.marcandoListo[pedido.id] = true;

      try {
        await this.restauranteService.marcarPedidoListo(pedido.id);

        this.$toast.add({
          severity: "success",
          summary: "Listo",
          detail: `Pedido #${pedido.id} listo para servir`,
          life: 2000,
        });

        await this.cargarPedidos(false);
      } catch (error) {
        console.error(error);
        this.$toast.add({
          severity: "error",
          summary: "Error",
          detail:
            error.response?.data?.message || "No se pudo marcar como listo",
          life: 3000,
        });
      } finally {
        this.marcandoListo[pedido.id] = false;
      }
    },
    async cargarPedidos(silencioso = false) {
      if (!silencioso) this.cargando = true;
      try {
        const response = await this.restauranteService.getPedidosCocina();
        let dataServer = response.pedidos || response.data || response || [];
        
        this.omitirCocina = response.omitir_cocina || false;

        if (!Array.isArray(dataServer)) dataServer = [];

        // Filtrar cancelados (0) y entregados (4)
        dataServer = dataServer.filter((p) => p.estado !== 0 && p.estado !== 4);

        // NUEVO: Solo notificar si el navegador está visible y hay nuevos pedidos
        if (
          !this.primeraCarga &&
          this.documentVisible &&
          dataServer.length > this.pedidos.length
        ) {
          this.playNotification();
        }

        this.pedidos = dataServer.map((pedido) => ({
          ...pedido,
          estado_cocina:
            parseInt(pedido.estado_cocina) || parseInt(pedido.estado) || 1,
        }));
      } catch (error) {
        console.error("Error al cargar:", error);
      } finally {
        this.cargando = false;
      }
    },

    esPedidoDemorado(pedido) {
      const diff = (new Date() - new Date(pedido.created_at)) / 60000;
      return diff > 15;
    },

    getEstadoClass(pedido) {
      if (this.esPedidoDemorado(pedido)) return "pedido-demorado";
      return "pedido-en-cocina";
    },

    getEstadoBadgeClass(pedido) {
      if (this.esPedidoDemorado(pedido)) return "badge-demorado";
      return "badge-en-cocina";
    },

    getEstadoTexto(pedido) {
      if (this.esPedidoDemorado(pedido)) return "¡URGENTE!";
      return "EN COCINA";
    },

    getTiempoTranscurrido(fecha) {
      if (!fecha) return "--";
      const diff = Math.floor((new Date() - new Date(fecha)) / 60000);
      if (diff < 1) return "Ahora";
      if (diff < 60) return `Hace ${diff} min`;
      const horas = Math.floor(diff / 60);
      const mins = diff % 60;
      return `Hace ${horas}h ${mins}m`;
    },

    actualizarHora() {
      this.horaActual = new Date().toLocaleTimeString("es-BO", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    playNotification() {
      // NUEVO: Solo reproducir si el navegador está visible
      if (!this.documentVisible) {
        console.log("Tab no visible - audio omitido");
        return;
      }

      try {
        audioNotificacion.play();
      } catch (e) {
        console.log("Audio bloqueado:", e);
      }
    },

    cerrarSession() {
      this.auth.userLogout();
    },
  },
};
</script>

<style scoped>
.cocina-container {
  min-height: 100vh;
  background: #f1f5f9;
  padding-bottom: 2rem;
}

.cocina-header {
  background: #1e293b;
  padding: 1rem 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
}

.cocina-filtros {
  padding: 0 2rem 1rem 2rem;
  position: sticky;
  top: 0;
  z-index: 10;
  background: #f1f5f9;
  padding-top: 1rem;
}

.btn-filtro {
  background: white !important;
  color: #64748b !important;
  border: 2px solid #e2e8f0 !important;
  font-weight: 600;
}

.btn-filtro:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
}

.btn-filtro-activo {
  background: #1e293b !important;
  color: white !important;
  border: 2px solid #1e293b !important;
  font-weight: 700;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);
}

.btn-filtro-pendiente-activo {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  color: white !important;
  border: none !important;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
}

.pedidos-grid {
  padding: 0 2rem;
  align-items: stretch;
}

.pedido-card {
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border-left: 5px solid transparent;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pedido-en-cocina {
  border-left-color: #f59e0b;
}

.pedido-demorado {
  border-left-color: #ef4444;
  background: #fef2f2;
  animation: pulse-red 2s infinite;
}

@keyframes pulse-red {
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(239, 68, 68, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
}

.pedido-card-header {
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid #e2e8f0;
}

.pedido-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 99px;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-en-cocina {
  background: #fffbeb;
  color: #b45309;
}

.badge-demorado {
  background: #ef4444;
  color: white;
}

.pedido-items {
  padding: 1rem;
  flex: 1;
}

.pedido-item {
  padding: 0.5rem 0;
  border-bottom: 1px dashed #e2e8f0;
}

.pedido-item:last-child {
  border-bottom: none;
}

.item-cantidad {
  background: #334155;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: bold;
  font-size: 1.5rem;
  margin-right: 0.5rem;
}

.item-nombre {
  font-size: 1.5rem;
  text-transform: uppercase;
  font-weight: 600;
  color: #1e293b;
}

.item-nota {
  font-size: 1.5rem;
  color: #d97706;
  background: #fffbeb;
  padding: 2px 6px;
  border-radius: 4px;
  margin-top: 4px;
  display: inline-block;
}

.contents {
  display: contents;
}

.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.list-leave-active {
  position: absolute;
}

.logout-dialog :deep(.p-dialog-header) {
  background: linear-gradient(135deg, #ef4444, #b91c1c);
  color: white;
  border: none;
  padding: 1.25rem;
}

.logout-dialog :deep(.p-dialog-title) {
  font-weight: 600;
  font-size: 1.1rem;
  letter-spacing: 0.3px;
}

.logout-dialog :deep(.p-dialog-header-close) {
  color: white;
}

.logout-dialog :deep(.p-dialog-header-close:hover) {
  background: rgba(255, 255, 255, 0.2);
}

.logout-dialog :deep(.p-dialog-content) {
  padding: 2rem;
}

.logout-dialog :deep(.p-dialog-footer) {
  border-top: 1px solid #e9ecef;
  padding: 1.5rem;
  gap: 0.75rem;
}

.dialog-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.dialog-icon {
  font-size: 5rem;
  color: #f59e0b;
  animation: pulse-icon 2s infinite;
}

@keyframes pulse-icon {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

.dialog-message {
  font-size: 1.2rem;
  color: #2c3e50;
  margin: 0;
  line-height: 1.6;
  font-weight: 500;
}

.pedido-mesa-numero-linea {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.8rem;
  font-weight: 900;
  line-height: 1.2;
}

.mesa-texto {
  color: #1e293b;
}

.separador {
  color: #94a3b8;
  font-weight: 400;
}

.numero-pedido {
  color: #000000;
}

.pedido-tiempo {
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 0.25rem;
}

.pedido-nota-general {
  background: #fef3c7;
  border-left: 3px solid #f59e0b;
  padding: 0.5rem;
  margin-top: 0.5rem;
  font-size: 1.5rem;
  color: #92400e;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pedido-nota-general strong {
  color: #b45309;
}

.pedido-cobrado-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: #dcfce7;
  color: #16a34a;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 8px;
  margin-top: 0.25rem;
}
.btn-filtro-listo-activo {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border: none !important;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}
.pedido-listo-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: #dcfce7;
  color: #16a34a;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 8px;
}
</style>
